import React, { FC, MouseEvent, useEffect, useRef, useState } from 'react'

import clsx from 'clsx'
import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { useClickAway } from 'react-use'
import styled from 'styled-components'

import { Icon, LineLimitTxt, SimplePlane } from 'src/components/atoms'
import { CommonContentsIconSelector } from 'src/components/organisms'

import { CommonContentPageIcons } from 'src/constants'
import { yupResolver } from 'src/customYup'
import {
  useCommonContentPage,
  useCommonContentPageLoading,
  useGetCommonContentPages,
  useToast,
  useAuthority,
} from 'src/hooks'
import { Routes } from 'src/routes'
import {
  CommonContentPageFormEntity,
  CommonContentPageFormEntitySchema,
  SelectableCommonContentPageEntity,
} from 'src/types'

const Container = styled.div<{ isSelected: boolean }>`
  height: 3.86rem;
  display: flex;
  align-items: center;
  padding: var(--s0);
  cursor: ${({ isSelected }) => (isSelected ? 'default' : 'pointer')};
  transition: background-color 0.2s ease-in-out;
`

type Props = {
  commonContent: SelectableCommonContentPageEntity
  className?: string
}

export const CommonContentsNavigationItem: FC<Props> = ({ commonContent, className }) => {
  const [isEditMode, setIsEditMode] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [showIconSelector, setShowIconSelector] = useState(false)

  const { refetch } = useGetCommonContentPages()
  const { toCommonContentPageFormEntity, updateCommonContentPage, updateCommonContentIcon } =
    useCommonContentPage()
  const { displayErrorTextToast, displayStandardTextToast } = useToast()
  const { showCommonContentPageLoading, hideCommonContentPageLoading } =
    useCommonContentPageLoading()
  const { canWriteCommonContent } = useAuthority()

  const { register, watch, setFocus } = useForm<CommonContentPageFormEntity>({
    resolver: yupResolver(CommonContentPageFormEntitySchema),
    defaultValues: toCommonContentPageFormEntity(commonContent),
  })

  const router = useRouter()

  useEffect(() => {
    if (isEditMode) {
      setFocus('name')
    }
  }, [isEditMode, setFocus])

  const handleOnSelect = async () => {
    if (commonContent.isSelected) {
      return
    }

    await router.push(
      Routes.ProjectCommonContentDetail.buildPath({ commonContentId: commonContent.id })
    )
  }

  const handleOnSave = async () => {
    setIsEditMode(false)
    if (commonContent.name === watch('name')) {
      return
    }

    showCommonContentPageLoading()

    try {
      setIsUploading(true)

      const formEntity = { ...toCommonContentPageFormEntity(commonContent), name: watch('name') }
      await updateCommonContentPage({ commonContentPageId: commonContent.id, formEntity })
      refetch()
      displayStandardTextToast('保存しました')
    } catch {
      setIsUploading(false)
      displayErrorTextToast('コンテンツページ名の変更に失敗しました')
    } finally {
      hideCommonContentPageLoading()
    }
  }

  const handleOnClickIcon = (event: MouseEvent) => {
    event.stopPropagation()
    setShowIconSelector(previous => !previous)
  }

  const handleSelectIcon = async (iconId: number) => {
    showCommonContentPageLoading()
    setShowIconSelector(false)

    try {
      await updateCommonContentIcon({ commonContentPage: commonContent, newIconId: iconId })
      displayStandardTextToast('保存しました')
      refetch()
    } catch {
      displayErrorTextToast('アイコンの更新に失敗しました')
    } finally {
      hideCommonContentPageLoading()
    }
  }

  const ref = useRef(null)
  useClickAway(ref, () => {
    if (showIconSelector) {
      setShowIconSelector(false)
    }
  })

  return (
    <div className={className}>
      <Container
        isSelected={commonContent.isSelected}
        onClick={handleOnSelect}
        className={clsx(
          commonContent.isSelected
            ? 'tw-bg-roovPrimary-default hover:tw-bg-roovPrimary-activePressed'
            : 'tw-bg-surface-primary hover:tw-bg-surface-secondary'
        )}
      >
        <div className="tw-full-justify tw-w-full">
          <div
            className={clsx(
              'tw-flex tw-items-center tw-flex-nowrap tw-gap-s-2',
              commonContent.isSelected
                ? 'tw-text-inlineText-primary'
                : 'tw-text-standardText-primary'
            )}
          >
            <div className="tw-w-[1.2rem] tw-aspect-square tw-flex-shrink-0">
              {/* overflow回避策としてrelativeを指定している親要素に対してabsoluteを持たせる */}
              <div className="tw-absolute">
                <div
                  role="presentation"
                  onClick={event =>
                    commonContent.isSelected && canWriteCommonContent && handleOnClickIcon(event)
                  }
                  className="tw-relative"
                  ref={ref}
                >
                  <Icon
                    size="--r1"
                    path={
                      CommonContentPageIcons.find(icon => icon.id === commonContent.icon)
                        ?.iconPath ?? ''
                    }
                    className={`${
                      commonContent.isSelected
                        ? 'tw-cursor-pointer tw-opacity-60 hover:tw-opacity-100'
                        : ''
                    }`}
                  />

                  {showIconSelector && (
                    <CommonContentsIconSelector
                      selectedIconId={commonContent.icon}
                      onClickClose={() => setShowIconSelector(false)}
                      selectIcon={handleSelectIcon}
                      className="tw-absolute tw-top-s2 -tw-left-s7 tw-z-30"
                    />
                  )}
                </div>
              </div>
            </div>

            {isEditMode ? (
              <SimplePlane
                className="!tw-w-fit tw-pr-s-1 tw-font-bold"
                {...register('name')}
                onBlur={handleOnSave}
                onEscapeKeyDown={() => setIsEditMode(false)}
                onEnterKeyDown={handleOnSave}
                mode="dark"
              />
            ) : (
              <LineLimitTxt
                maxLineLimit={1}
                className={`tw-pr-s-1 ${commonContent.isSelected ? 'tw-font-bold' : ''}`}
              >
                {isUploading ? watch('name') : commonContent.name}
              </LineLimitTxt>
            )}
          </div>
          {commonContent.isSelected && canWriteCommonContent && (
            <div className="tw-text-inlineText-primary tw-cursor-pointer">
              {isEditMode ? (
                <button
                  type="button"
                  className="tw-outline-none"
                  onClick={() => setIsEditMode(false)}
                >
                  <Icon size="--r1" path="close/soft/line" />
                </button>
              ) : (
                <button
                  type="button"
                  className="tw-outline-none"
                  onClick={() => setIsEditMode(true)}
                >
                  <Icon size="--r1" path="edit-3/soft/line" className="tw-opacity-60" />
                </button>
              )}
            </div>
          )}
        </div>
      </Container>
    </div>
  )
}
